import Errors from 'Components/Errors'
import SaveDialog from 'Components/SaveDialog'
import StyledForm from 'Components/StyledForm'
import {
  changeFaultReportUpdate,
  createFaultReportUpdate,
  type FaultReportUpdateShape,
  fetchFaultReportUpdates
} from 'Services/SalesManagement/Features/FaultReport/faultReportSlice'
import { useAppDispatch } from 'Store'
import SnackbarUtils from 'Util/Snackbar'
import { Box, TextField } from '@mui/material'
import { unwrapResult } from '@reduxjs/toolkit'
import { type FC, type FormEvent, useEffect, useRef, useState } from 'react'
import { t } from 'ttag'

const SaveFaultReportUpdateDialog: FC<{
  faultReportId: number
  data: FaultReportUpdateShape
  open: boolean
  loading: boolean
  handleClose: () => void
}> = ({ faultReportId, data, open, loading, handleClose }) => {
  const dispatch = useAppDispatch()
  const formRef = useRef<HTMLFormElement>(null)

  const initialState: FaultReportUpdateShape = {
    id: null,
    description_fi: '',
    updated: null
  }

  const [faultReportUpdateState, setFaultReportUpdateState] = useState<FaultReportUpdateShape>(
    data || initialState
  )

  const errorsDefaultState = {
    description_fi: []
  }

  const isNewInstance = !data?.id

  const [errors, setErrors] = useState(errorsDefaultState)

  // set existing data to form fields and empty errors when modal opens
  useEffect(() => {
    if (open) {
      setErrors(errorsDefaultState)
      if (isNewInstance) {
        setFaultReportUpdateState(initialState)
      } else {
        setFaultReportUpdateState(data)
      }
    }
  }, [open, data])

  const handleSubmit = () => {
    formRef.current?.requestSubmit()
  }

  const handleSaveSuccess = (isCreateAction: boolean) => {
    handleClose()
    dispatch(
      fetchFaultReportUpdates({
        faultReportId
      })
    )
    if (isCreateAction) {
      SnackbarUtils.success(t`Fault report update successfully added.`)
    } else {
      SnackbarUtils.success(t`Fault report update successfully updated.`)
    }
  }

  const handleSave = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const payload = {
      faultReportId,
      ...faultReportUpdateState
    }
    const saveAction = isNewInstance
      ? createFaultReportUpdate(payload)
      : changeFaultReportUpdate(payload)
    dispatch(saveAction)
      .then(unwrapResult)
      .then(() => {
        handleSaveSuccess(isNewInstance)
      })
      .catch(error => {
        if (error) {
          setErrors({
            ...error
          })
        }
        SnackbarUtils.error(t`There was an error saving the fault report update.`)
      })
  }

  return (
    <SaveDialog
      open={open}
      handleClose={handleClose}
      handleSubmit={handleSubmit}
      loading={loading}
      title={t`Save fault report update`}
    >
      <StyledForm onSubmit={handleSave} formRef={formRef} isDisabled={loading}>
        <Box>
          <TextField
            sx={{ width: '100%' }}
            type="text"
            label={t`Description`}
            value={faultReportUpdateState.description_fi}
            multiline
            minRows={4}
            onChange={e =>
              setFaultReportUpdateState(prevState => ({
                ...prevState,
                description_fi: e.target.value
              }))
            }
          />
          <Errors error={errors.description_fi} />
        </Box>
      </StyledForm>
    </SaveDialog>
  )
}

export { SaveFaultReportUpdateDialog }
